import { createSelector } from 'reselect';

import {
  DeviceTargetEnum,
  EXTENSION_BY_MODE,
  DeviceOwnershipEnum,
  DEVICE_TARGET_ODITOO,
  ALLOWED_DEVICE_EXTENSIONS,
} from 'modules/devices/constants';
import { ExtensionEnum } from 'modules/global/typing/enums/extensions.enum';
import { getSelectedGroupId } from 'modules/groups/selectors';
import { isGroupManagement } from 'modules/router/selectors';
import type { RootState } from 'reducers/index';
import { getDealerById, getDealersByIds, getSelectedDealerId } from 'selectors/dealerSelectors';
import { getProps, getRoot } from 'utils/selectors';

export const getState = (state: RootState) => state.devices;

export const getUI = createSelector(getState, state => state.ui);

const getAll = createSelector(getState, state => state.data.entities);

const getDetailsState = createSelector(getUI, ui => ui.details);

export const getDeviceById = createSelector(getAll, getProps<string>, (entries, deviceId) => entries[deviceId]);

const getUserDealerIdsByDeviceId = createSelector(getDeviceById, device => device?.userDealerIds);

const getUserDealersByDeviceId = createSelector(getRoot, getUserDealerIdsByDeviceId, getDealersByIds);

export const getSortedUserDealersByDeviceId = createSelector(
  getUserDealersByDeviceId,
  getProps<string>,
  (dealers: Array<{ id: string; name: string }>) =>
    dealers.toSorted((dealer, otherDealer) => dealer.name.localeCompare(otherDealer.name))
);

export const getSelectedDeviceId = createSelector(getDetailsState, ({ selectedId }) => selectedId);

export const isUpdateModalVisible = createSelector(getDetailsState, ({ visibleModal }) => visibleModal);

export const isPatching = createSelector(getDetailsState, ({ patching }) => patching);

export const getPatchingError = createSelector(getDetailsState, ({ patchingError }) => patchingError);

export const getSelectedDevice = createSelector(getRoot, getSelectedDeviceId, (root, selectedId) =>
  (selectedId ? getDeviceById(root, selectedId) : undefined));

const fleetbackAppEnable = createSelector(getDealerById, dealer =>
  dealer?.extensions?.some((extension: string) => ALLOWED_DEVICE_EXTENSIONS.includes(extension)));

const oditooAppEnable = createSelector(getDealerById, dealer => dealer?.extensions?.includes(ExtensionEnum.AUDIT));

const getEnableTargetsByDealerId = createSelector(fleetbackAppEnable, oditooAppEnable, (haveFBApp, haveOditooApp) => [
  ...(haveFBApp ? [DeviceTargetEnum.FLEETBACK] : []),
  ...(haveOditooApp ? [DEVICE_TARGET_ODITOO] : []),
]);

export const getEnabledTargets = createSelector(
  getEnableTargetsByDealerId,
  isGroupManagement,
  getProps<string>,
  (targets, isGroup) => (isGroup ? [DeviceTargetEnum.FLEETBACK] : targets)
);

export const getAvailableDeviceMode = createSelector(
  getDealerById,
  fleetbackAppEnable,
  oditooAppEnable,
  getProps<string>,
  dealer => (
    Object.entries(EXTENSION_BY_MODE).reduce<string[]>((acc, [mode, extension]) => {
      if (dealer?.extensions?.includes(extension)) {
        acc.push(mode);
      }
      return acc;
    }, [])
  )
);

const getSelectedDealerEnableTargets = createSelector(getRoot, getSelectedDealerId, getEnableTargetsByDealerId);

const getDefaultDealerFormValues = createSelector(
  getSelectedDealerId,
  getSelectedGroupId,
  getSelectedDealerEnableTargets,
  (dealerId, groupId, [target]) => ({
    dealerId,
    groupId,
    target,
    ownership: target === DeviceTargetEnum.FLEETBACK ? DeviceOwnershipEnum.SHARED : undefined,
  })
);

const getDefaultGroupFormValues = createSelector(getSelectedGroupId, groupId => ({
  groupId,
  target: DeviceTargetEnum.FLEETBACK,
  ownership: DeviceOwnershipEnum.PERSONAL,
}));

export const getDefaultFormValues = createSelector(
  isGroupManagement,
  getDefaultGroupFormValues,
  getDefaultDealerFormValues,
  (isGroupPage, defaultGroupDevice, defaultDealerDevice) => (isGroupPage ? defaultGroupDevice : defaultDealerDevice)
);
